import { ReactNode } from "react";

interface AIReadableSectionProps {
  title: string;
  summary: string[];
  children?: ReactNode;
  className?: string;
}

/**
 * AI-Readable Section Component
 * Provides structured content that LLMs can easily extract and understand.
 * Includes semantic HTML, clear headings, and bullet-point summaries.
 */
const AIReadableSection = ({ title, summary, children, className = "" }: AIReadableSectionProps) => {
  return (
    <section className={`py-12 bg-muted/30 ${className}`} aria-labelledby="quick-summary">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <header className="mb-6">
            <h2 id="quick-summary" className="text-2xl font-bold text-primary mb-4">
              {title}
            </h2>
          </header>
          
          {/* Quick Summary - Optimized for AI extraction */}
          <div className="bg-background rounded-lg border border-border p-6 mb-6">
            <h3 className="sr-only">Quick Summary Points</h3>
            <ul className="space-y-3" role="list">
              {summary.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-foreground">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {children && (
            <div className="prose prose-slate max-w-none">
              {children}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AIReadableSection;
